import React, { useState } from 'react';
import { X, Key, UserPlus, Globe, Send, CheckCircle, Lightbulb, ShieldCheck, Copy, RefreshCw, LogInIcon, Search, SearchCheck, Section, SquareDashedMousePointer, Info, OctagonAlert } from 'lucide-react';

const StepCard = ({ number, title, icon: Icon, image, children, onImageClick }: any) => (
    <div className="relative pl-14 pb-12 border-l-2 border-white/10 last:border-0 last:pb-0 ml-4">
        <div className="absolute left-[-21px] top-0 w-10 h-10 bg-[rgb(var(--c-bg))] border-2 border-cyan-500/50 rounded-full flex items-center justify-center text-cyan-400 font-bold z-10 shadow-[0_0_15px_rgba(34,211,238,0.3)]">
            {number}
        </div>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-[2.5rem] hover:bg-white/10 transition-all duration-300 group">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-8 space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                        <Icon className="text-cyan-400" size={22} />
                        <h3 className="text-xl font-bold text-white tracking-tight">{title}</h3>
                    </div>
                    <div className="text-gray-400 text-sm leading-relaxed space-y-4">
                        {children}
                    </div>
                </div>

                {image && (
                    <div className="md:col-span-4 flex items-start justify-center pt-2">
                        <div
                            className="relative group/img cursor-zoom-in"
                            onClick={() => onImageClick(image)}
                        >
                            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl blur opacity-20 group-hover/img:opacity-50 transition duration-500"></div>

                            <div className="relative bg-[rgb(var(--c-bg))] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src={image}
                                    alt="Referencia"
                                    className="w-full h-auto object-cover opacity-80 group-hover/img:opacity-100 group-hover/img:scale-110 transition-all duration-500"
                                    style={{ maxWidth: '160px' }}
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                    <UserPlus className="text-white" size={24} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const ActivAPi = () => {
    const [isSimulating, setIsSimulating] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [selectedImg, setSelectedImg] = useState<string | null>(null);

    const simulateToken = () => {
        setIsSimulating(true);
        setTimeout(() => {
            setIsSimulating(false);
            setShowToken(true);
        }, 1500);
    };

    return (
        <div className="max-w-none mx-auto animate-fadeIn overflow-y-auto custom-scrollbar px-6 py-4 h-full">
            <header className="mb-12">
                <h2 className="text-4xl font-extrabold text-white mb-3">Habilitación APIs Ariba</h2>
                <p className="text-gray-500 text-lg font-light">Siguientes pasos para la habilitación de las APIs </p>
            </header>

            <div className="space-y-2">
                {/* PASO 1 */}
                <StepCard number="1" title="Acceder a Developer Portal" icon={Key} image="/assets/tutorial/token.png" onImageClick={setSelectedImg}>
                    <p>Debe iniciar sesión en el sistema <a href="https://developer.ariba.com" target="_blank" className="text-cyan-400 underline">Portal de Desarrolladores de Ariba</a>.</p>

                    <div className="mt-4 p-4 bg-green-500/10 border border-500/20 rounded-2xl flex gap-4">
                        <Lightbulb className="text-green-400 shrink-0" size={20} />
                        <p className="text-xs italic text-green-200/70">debe ingresar el token de la aplicación movil el cual registro durante la habilitación del portal.</p>
                    </div>
                </StepCard>

                {/* PASO 2 - INTERACTIVO */}
                <StepCard number="2" title="Ir a la Gestion de las aplicaciones" icon={ShieldCheck} image="/assets/tutorial/crearapp1.png" onImageClick={setSelectedImg}>
                    <p>Ingresar a la opción <span className="text-white">Manage</span></p>
                    <p>tercer icono, parte izquierda del menú </p>

                </StepCard>

                {/* PASO 3 */}
                <StepCard number="3" title="Crear aplicación" icon={ShieldCheck} image="/assets/tutorial/crearapp2.png" onImageClick={setSelectedImg}>
                    <p>Dar clic en la opción "Add" para crear la aplicación que contendra la API requerida</p>
                </StepCard>

                {/* PASO 4 */}
                <StepCard number="4" title="Ingresar los datos necesarios" icon={Send} image="/assets/tutorial/crearapp3.png" onImageClick={setSelectedImg}>
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-4 p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs flex items-center justify-center font-bold border border-cyan-500/30">1</span>
                            <p className="text-sm text-gray-300">
                                Define el <span className="px-2 py-1 bg-cyan-500/10 rounded text-cyan-400 text-xs font-mono border border-cyan-500/20">Application Name</span>
                            </p>
                        </div>

                        <div className="flex items-center gap-4 p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-xs flex items-center justify-center font-bold border border-cyan-500/30">2</span>
                            <p className="text-sm text-gray-300">
                                Escribe la <span className="px-2 py-1 bg-cyan-500/10 rounded text-cyan-400 text-xs font-mono border border-cyan-500/20">Description</span>
                            </p>
                        </div>
                    </div>
                </StepCard>
                {/* PASO 5 */}

                <StepCard number="5" title="Asignar API" icon={SquareDashedMousePointer} image="/assets/tutorial/crearapp4.png" onImageClick={setSelectedImg} >
                    <p>Ir a la opción "Actions -- Request API Access"</p>

                </StepCard>

                <StepCard number="6" title="Buscar la API" icon={SearchCheck} image="/assets/tutorial/crearapp5.png" onImageClick={setSelectedImg} >
                    <p>Buscar la API que se necesita habilitar</p>
                    <div className="mt-6 flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                        <Info className="text-blue-400" size={24} />
                        <span className="text-sm text-blue-200/80 font-medium">Solo se habilita una API por aplicación</span>
                    </div>
                </StepCard>

                <StepCard number="7" title="Asignar API a la solución" icon={SearchCheck} image="/assets/tutorial/crearapp6.png" onImageClick={setSelectedImg} >
                    <p>Asignar la API a una solución de SAP Ariba</p>
                    <div className="mt-6 flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                        <Info className="text-blue-400" size={24} />
                        <span className="text-sm text-blue-200/80 font-medium">dependiendo de la API se habilita solo para la solución que se desarrolló</span>
                    </div>
                </StepCard>

                <StepCard number="8" title="Proceso de aprobación" icon={SearchCheck} image="/assets/tutorial/crearapp7.png" onImageClick={setSelectedImg} >
                    <p>La Aplicación pasa a un proceso interno de aprobación por parte del equipo de soporte de Ariba, esto suele tarde de 24 a 36 horas habiles</p>
                    <div className="mt-6 flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                        <Info className="text-blue-400" size={24} />
                        <span className="text-sm text-blue-200/80 font-medium">dependiendo de la API el equipo de soporte puede solicitar mas información mediante correo electronico</span>
                    </div>
                </StepCard>

                <StepCard number="8" title="API Aprobada" icon={SearchCheck} image="/assets/tutorial/crearapp8.png" onImageClick={setSelectedImg} >
                    <p>Cuando el equipo de soporte realice la aprobación de la API, el flujo inferior pasará a su último nodo</p>
                </StepCard>

                <StepCard number="9" title="Obtener las credenciales" icon={SearchCheck} image="/assets/tutorial/crearapp9.png" onImageClick={setSelectedImg} >
                    <p>El último paso es obtener las credenciales las cuales se usaran para solicitar el token con el que se extraera la información de la API</p>
                    <div className="mt-6 flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                        <Info className="text-blue-400" size={24} />
                        <span className="text-sm text-blue-200/80 font-medium">Las credenciales solo se pueden ver durante el proceso, si por algun motivo se pierden dichas credenciales se deberan solicitar nuevas realizando estos mismos pasos</span>
                    </div>
                    <div className="mt-6 flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl">
                        <OctagonAlert className="text-yellow-400" size={24} />
                        <span className="text-sm text-yellow-200/80 font-medium">Las credenciales anteriores se perderan y el usuario debera reiniciarlas en los sistemas donde ya se realiza la ejecución de la API</span>
                    </div>
                </StepCard>
                {selectedImg && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-zoom-out animate-fadeIn"
                        onClick={() => setSelectedImg(null)}
                    >
                        <button className="absolute top-10 right-10 text-white/50 hover:text-white transition-colors">
                            <X size={40} />
                        </button>
                        <img
                            src={selectedImg}
                            alt="Zoom"
                            className="max-w-full max-h-full rounded-2xl shadow-2xl border border-white/10 animate-scaleIn"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivAPi;

